/**
 * Bonsait Animation Engine — Web Animations API (WAAPI) based.
 * Zero dependencies.
 *
 * Two tiers:
 *   1. Data-attribute driven  — drop <div data-animate="reveal-up"> into HTML
 *   2. Imperative JS API       — Bonsait.Animations.reveal(el), .tilt(el), etc.
 *
 * ── Data-Attribute Usage (no JS needed) ─────────────────────────────────────
 *
 *   <div data-animate="reveal-up" data-animate-duration="600"
 *        data-animate-easing="spring:200:20" data-animate-trigger="scroll"
 *        data-animate-stagger="100">
 *     <div class="card">...</div>
 *     <div class="card">...</div>
 *   </div>
 *
 *   Attributes:
 *     data-animate          — preset name (required)
 *     data-animate-duration — ms (default: preset-specific, usually 600)
 *     data-animate-delay    — ms (default: 0)
 *     data-animate-easing   — CSS easing or "spring:S:D" (default: ease-out)
 *     data-animate-trigger  — scroll | load | hover | click (default: scroll)
 *     data-animate-stagger  — ms between child animations (default: 0)
 *     data-animate-scroll-linked — "true" for scroll-driven progress
 *
 * ── Imperative JS API ───────────────────────────────────────────────────────
 *
 *   // Basic — play any preset
 *   Bonsait.Animations.create(el, 'fade-in-up', { duration: 800 });
 *
 *   // Scroll-linked
 *   Bonsait.Animations.scrollLinked(el, 'scale-reveal');
 *
 *   // Stagger children
 *   Bonsait.Animations.stagger(container, 'reveal-up', { stagger: 120 });
 *
 *   // Interactive hover elevation
 *   Bonsait.Animations.elevate(el, { shadow: '0 8px 30px rgba(0,0,0,0.12)' });
 *
 *   // 3D magnetic tilt
 *   Bonsait.Animations.tilt(el, { maxRotate: 8 });
 *
 *   // Button press feedback
 *   Bonsait.Animations.press(el);
 *
 *   // Button with loading state
 *   Bonsait.Animations.loadingButton(el, fetch('/api/submit'));
 *
 *   // Skeleton shimmer placeholder
 *   Bonsait.Animations.shimmer(el, { lines: 4, gap: 12 });
 *
 *   // Form field validation
 *   Bonsait.Animations.fieldValidate(input, 'error');   // shake
 *   Bonsait.Animations.fieldValidate(input, 'success'); // green checkmark
 *
 *   // Float label
 *   Bonsait.Animations.fieldFloat(input);
 *
 * ── Preset Reference ────────────────────────────────────────────────────────
 *
 *   Block Reveals     reveal, reveal-up, reveal-scale
 *   Fading            fade-in, fade-in-up/down/left/right
 *   Sliding           slide-in-up/down/left/right
 *   Zooming           zoom-in, zoom-in-up, scale-reveal
 *   Interactive       elevate, glow, tilt         (hover trigger)
 *   Button/CTA        press, sweep                (click trigger)
 *   Attention         pulse, shake, shake-x, bounce
 *   Exit              fade-out, fade-out-up
 */

'use strict';

// ═══════════════════════════════════════════════════════════════════════════
// Presets
// ═══════════════════════════════════════════════════════════════════════════

var PRESETS = {

    // ── Block Reveals ─────────────────────────────────────────────────────

    'reveal': {
      keyframes: [
        { opacity: 0, transform: 'translate3d(0, 24px, 0) scale3d(0.96, 0.96, 1)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0) scale3d(1, 1, 1)' }
      ],
      defaults: { duration: 700, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    },

    'reveal-up': {
      keyframes: [
        { opacity: 0, transform: 'translate3d(0, 48px, 0)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' }
      ],
      defaults: { duration: 700, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    },

    'reveal-scale': {
      keyframes: [
        { opacity: 0, transform: 'scale3d(0.92, 0.92, 1)' },
        { opacity: 1, transform: 'scale3d(1, 1, 1)' }
      ],
      defaults: { duration: 600, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    },

    // ── Fading ────────────────────────────────────────────────────────────

    'fade-in': {
      keyframes: [{ opacity: 0 }, { opacity: 1 }],
      defaults: { duration: 600, easing: 'ease-out' }
    },
    'fade-in-up': {
      keyframes: [
        { opacity: 0, transform: 'translate3d(0, 40px, 0)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' }
      ],
      defaults: { duration: 600, easing: 'ease-out' }
    },
    'fade-in-down': {
      keyframes: [
        { opacity: 0, transform: 'translate3d(0, -40px, 0)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' }
      ],
      defaults: { duration: 600, easing: 'ease-out' }
    },
    'fade-in-left': {
      keyframes: [
        { opacity: 0, transform: 'translate3d(-40px, 0, 0)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' }
      ],
      defaults: { duration: 600, easing: 'ease-out' }
    },
    'fade-in-right': {
      keyframes: [
        { opacity: 0, transform: 'translate3d(40px, 0, 0)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' }
      ],
      defaults: { duration: 600, easing: 'ease-out' }
    },

    // ── Exit (reverse of fade-in) ─────────────────────────────────────────

    'fade-out': {
      keyframes: [{ opacity: 1 }, { opacity: 0 }],
      defaults: { duration: 400, easing: 'ease-in' }
    },
    'fade-out-up': {
      keyframes: [
        { opacity: 1, transform: 'translate3d(0, 0, 0)' },
        { opacity: 0, transform: 'translate3d(0, -40px, 0)' }
      ],
      defaults: { duration: 400, easing: 'ease-in' }
    },

    // ── Sliding ───────────────────────────────────────────────────────────

    'slide-in-up': {
      keyframes: [
        { transform: 'translate3d(0, 100%, 0)', visibility: 'visible' },
        { transform: 'translate3d(0, 0, 0)', visibility: 'visible' }
      ],
      defaults: { duration: 600, easing: 'ease-out' }
    },
    'slide-in-down': {
      keyframes: [
        { transform: 'translate3d(0, -100%, 0)', visibility: 'visible' },
        { transform: 'translate3d(0, 0, 0)', visibility: 'visible' }
      ],
      defaults: { duration: 600, easing: 'ease-out' }
    },
    'slide-in-left': {
      keyframes: [
        { transform: 'translate3d(-100%, 0, 0)', visibility: 'visible' },
        { transform: 'translate3d(0, 0, 0)', visibility: 'visible' }
      ],
      defaults: { duration: 600, easing: 'ease-out' }
    },
    'slide-in-right': {
      keyframes: [
        { transform: 'translate3d(100%, 0, 0)', visibility: 'visible' },
        { transform: 'translate3d(0, 0, 0)', visibility: 'visible' }
      ],
      defaults: { duration: 600, easing: 'ease-out' }
    },

    // ── Zooming ───────────────────────────────────────────────────────────

    'zoom-in': {
      keyframes: [
        { opacity: 0, transform: 'scale3d(0.3, 0.3, 0.3)' },
        { opacity: 1, transform: 'scale3d(1, 1, 1)' }
      ],
      defaults: { duration: 600, easing: 'ease-out' }
    },
    'zoom-in-up': {
      keyframes: [
        { opacity: 0, transform: 'scale3d(0.3, 0.3, 0.3) translate3d(0, 40px, 0)' },
        { opacity: 1, transform: 'scale3d(1, 1, 1) translate3d(0, 0, 0)' }
      ],
      defaults: { duration: 600, easing: 'ease-out' }
    },
    'scale-reveal': {
      keyframes: [
        { transform: 'scale3d(0.8, 0.8, 1)', opacity: 0 },
        { transform: 'scale3d(1, 1, 1)', opacity: 1 }
      ],
      defaults: { duration: 1000, easing: 'ease-out' }
    },

    // ── Interactive hover ─────────────────────────────────────────────────

    'elevate': {
      keyframes: [
        { transform: 'translate3d(0, 0, 0)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
        { transform: 'translate3d(0, -4px, 0)', boxShadow: '0 12px 28px rgba(0,0,0,0.12)', offset: 0.4 },
        { transform: 'translate3d(0, -2px, 0)', boxShadow: '0 8px 22px rgba(0,0,0,0.10)' }
      ],
      defaults: { duration: 300, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    },

    'glow': {
      keyframes: [
        { boxShadow: '0 0 0 0 rgba(59, 130, 246, 0)' },
        { boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.25)' }
      ],
      defaults: { duration: 300, easing: 'ease-out' }
    },

    // ── Button / CTA feedback ─────────────────────────────────────────────

    'press': {
      keyframes: [
        { transform: 'scale3d(1, 1, 1)' },
        { transform: 'scale3d(0.96, 0.96, 1)', offset: 0.4 },
        { transform: 'scale3d(1, 1, 1)' }
      ],
      defaults: { duration: 200, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    },

    'sweep': {
      keyframes: [
        { backgroundPosition: '200% 50%' },
        { backgroundPosition: '-100% 50%' }
      ],
      defaults: { duration: 800, easing: 'ease-in-out', iterations: Infinity }
    },

    // ── Attention seekers ─────────────────────────────────────────────────

    'pulse': {
      keyframes: [
        { transform: 'scale3d(1, 1, 1)' },
        { transform: 'scale3d(1.05, 1.05, 1.05)', offset: 0.5 },
        { transform: 'scale3d(1, 1, 1)' }
      ],
      defaults: { duration: 1000, easing: 'ease-in-out', iterations: Infinity }
    },

    'shake': {
      keyframes: [
        { transform: 'translate3d(0, 0, 0)' },
        { transform: 'translate3d(-10px, 0, 0)', offset: 0.1 },
        { transform: 'translate3d(10px, 0, 0)', offset: 0.3 },
        { transform: 'translate3d(-10px, 0, 0)', offset: 0.5 },
        { transform: 'translate3d(10px, 0, 0)', offset: 0.7 },
        { transform: 'translate3d(-10px, 0, 0)', offset: 0.9 },
        { transform: 'translate3d(0, 0, 0)' }
      ],
      defaults: { duration: 800, easing: 'ease-in-out' }
    },

    'shake-x': {
      keyframes: [
        { transform: 'translate3d(0, 0, 0)' },
        { transform: 'translate3d(-6px, 0, 0)', offset: 0.15 },
        { transform: 'translate3d(6px, 0, 0)', offset: 0.35 },
        { transform: 'translate3d(-4px, 0, 0)', offset: 0.55 },
        { transform: 'translate3d(3px, 0, 0)', offset: 0.75 },
        { transform: 'translate3d(0, 0, 0)' }
      ],
      defaults: { duration: 500, easing: 'ease-in-out' }
    },

    'bounce': {
      keyframes: [
        { transform: 'translate3d(0, 0, 0)', offset: 0 },
        { transform: 'translate3d(0, -20px, 0)', offset: 0.3 },
        { transform: 'translate3d(0, -4px, 0)', offset: 0.5 },
        { transform: 'translate3d(0, -10px, 0)', offset: 0.7 },
        { transform: 'translate3d(0, 0, 0)', offset: 1 }
      ],
      defaults: { duration: 900, easing: 'ease-in-out' }
    }
  };

  var ANIMATE_CSS_MAP = {
    fadeIn: 'fade-in', fadeInUp: 'fade-in-up',
    fadeInDown: 'fade-in-down', fadeInLeft: 'fade-in-left',
    fadeInRight: 'fade-in-right', fadeInUpBig: 'fade-in-up',
    fadeInDownBig: 'fade-in-down', fadeInLeftBig: 'fade-in-left',
    fadeInRightBig: 'fade-in-right',
    slideInUp: 'slide-in-up', slideInDown: 'slide-in-down',
    slideInLeft: 'slide-in-left', slideInRight: 'slide-in-right',
    zoomIn: 'zoom-in', zoomInUp: 'zoom-in-up',
    pulse: 'pulse', shake: 'shake',
    bounceIn: 'zoom-in', bounceInUp: 'zoom-in-up',
    bounceInDown: 'zoom-in', bounceInLeft: 'zoom-in',
    bounceInRight: 'zoom-in',
    flipInX: 'fade-in', flipInY: 'fade-in',
    lightSpeedIn: 'slide-in-right',
    rotateIn: 'zoom-in', rollIn: 'slide-in-left'
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Utilities
  // ═══════════════════════════════════════════════════════════════════════════

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', function (e) {
    reducedMotion = e.matches;
  });

  function springEasing(stiffness, damping, mass) {
    stiffness = stiffness || 200;
    damping = damping || 20;
    mass = mass || 1;
    var zeta = damping / (2 * Math.sqrt(stiffness * mass));
    var x1 = Math.min(0.3, Math.max(0, 0.1 + (zeta * 0.15)));
    var y1 = Math.min(1, Math.max(0, 1.4 - (zeta * 0.7)));
    var x2 = Math.min(0.9, Math.max(0.5, 0.7 - (zeta * 0.3)));
    return 'cubic-bezier(' + x1.toFixed(2) + ',' + y1.toFixed(2) + ',' + x2.toFixed(2) + ',1.00)';
  }

  function parseEasing(raw) {
    if (!raw) { return 'ease-out'; }
    var m = raw.match(/^spring:(\d+):(\d+)(?::(\d+(?:\.\d+)?))?$/);
    if (m) {
      return springEasing(parseInt(m[1], 10), parseInt(m[2], 10), m[3] ? parseFloat(m[3]) : 1);
    }
    return raw;
  }

  function hasHover() {
    return window.matchMedia('(hover: hover)').matches;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Core
  // ═══════════════════════════════════════════════════════════════════════════

  function createAnimation(el, presetName, opts) {
    if (reducedMotion) { return null; }
    opts = opts || {};
    var preset = PRESETS[presetName];
    if (!preset) { console.warn('[Bonsait Animations] Unknown preset:', presetName); return null; }

    var anim = el.animate(preset.keyframes, {
      duration: opts.duration || preset.defaults.duration || 600,
      delay: opts.delay || 0,
      easing: parseEasing(opts.easing || preset.defaults.easing || 'ease-out'),
      iterations: opts.iterations || preset.defaults.iterations || 1,
      direction: opts.direction || 'normal',
      fill: opts.fill || 'both'
    });

    el._bonsaitAnimation = anim;
    return anim;
  }

  function staggerChildren(container, presetName, opts) {
    opts = opts || {};

    // Use custom selector if provided, otherwise direct children
    var children;
    if (opts.staggerSelector) {
      children = Array.from(container.querySelectorAll(opts.staggerSelector));
    } else {
      children = Array.from(container.children);
    }

    // Filter out elements that already have their own animation
    children = children.filter(function (c) {
      return !c.hasAttribute('data-animate');
    });
    if (children.length === 0) { return []; }

    var staggerMs = opts.stagger || 80;
    var animations = [];

    children.forEach(function (child, i) {
      var childOpts = Object.assign({}, opts);
      childOpts.delay = (opts.delay || 0) + (i * staggerMs);
      childOpts.stagger = 0;
      childOpts.staggerSelector = null;
      var anim = createAnimation(child, presetName, childOpts);
      if (anim) { animations.push(anim); }
    });

    return animations;
  }

  function createScrollLinked(el, presetName, opts) {
    opts = opts || {};
    var preset = PRESETS[presetName];
    if (!preset) { return null; }

    var anim = el.animate(preset.keyframes, {
      duration: opts.duration || preset.defaults.duration || 1000,
      easing: parseEasing(opts.easing),
      fill: 'both'
    });
    anim.pause();

    var scrollStart = opts.scrollStart || 0;
    var scrollEnd = opts.scrollEnd || 1;
    var clamp = opts.scrollClamp !== false;
    var ticking = false;

    function update() {
      var rect = el.getBoundingClientRect();
      var viewH = window.innerHeight;
      var raw = (viewH - rect.top) / (viewH + rect.height);
      var progress = (raw - scrollStart) / (scrollEnd - scrollStart);
      if (clamp) { progress = Math.max(0, Math.min(1, progress)); }
      anim.currentTime = anim.effect.getComputedTiming().duration * progress;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () { update(); ticking = false; });
        ticking = true;
      }
    }, { passive: true });

    update();
    el._bonsaitAnimation = anim;
    return anim;
  }

  function animateOut(el, presetName, opts, removeAfter) {
    opts = opts || {};
    var exitName = presetName;
    if (presetName.indexOf('-in') > -1) {
      exitName = presetName.replace('-in', '-out');
    } else if (presetName.indexOf('fade-out') === -1) {
      exitName = 'fade-out';
    }
    if (!PRESETS[exitName]) { exitName = 'fade-out'; }

    var anim = createAnimation(el, exitName, opts);
    if (anim && removeAfter !== false) {
      anim.onfinish = function () {
        if (el.parentNode) { el.parentNode.removeChild(el); }
      };
    }
    return anim;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ── 1. Block Reveals ──────────────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════════════

  function reveal(el, opts) {
    opts = opts || {};
    var preset = opts.preset || 'reveal';

    ensureObserver(opts);
    observedElements.set(el, {
      preset: preset, opts: opts, triggered: false,
      stagger: (opts.stagger > 0) ? opts.stagger : 0
    });
    observer.observe(el);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ── 2. Interactive Block & Card Elevation ─────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════════════

  function elevate(el, opts) {
    if (!hasHover()) { return; }
    opts = opts || {};
    var lift = opts.lift != null ? opts.lift : 4;
    var shadow = opts.shadow || '0 12px 28px rgba(0,0,0,0.10)';
    var duration = opts.duration || 300;
    var easing = parseEasing(opts.easing || 'cubic-bezier(0.22, 1, 0.36, 1)');
    var origTransform = el.style.transform || '';
    var origShadow = el.style.boxShadow || '';
    var origTransition = el.style.transition || '';
    var prevWillChange = el.style.willChange;

    function enter() {
      if (reducedMotion) { return; }
      el.style.willChange = 'transform, box-shadow';
      el.style.transition = 'transform ' + duration + 'ms ' + easing
        + ', box-shadow ' + duration + 'ms ' + easing;
      el.style.transform = 'translate3d(0, -' + lift + 'px, 0)';
      el.style.boxShadow = shadow;
    }

    function leave() {
      el.style.transition = 'transform ' + (duration * 1.2) + 'ms ' + easing
        + ', box-shadow ' + (duration * 1.2) + 'ms ' + easing;
      el.style.transform = origTransform;
      el.style.boxShadow = origShadow;
      var elRef = el;
      setTimeout(function () {
        elRef.style.willChange = prevWillChange;
        elRef.style.transition = origTransition;
      }, duration * 1.2);
    }

    el.addEventListener('mouseenter', enter);
    el.addEventListener('mouseleave', leave);
    el.addEventListener('touchstart', enter, { passive: true });
    el.addEventListener('touchend', leave);

    el._bonsaitElevateCleanup = function () {
      el.removeEventListener('mouseenter', enter);
      el.removeEventListener('mouseleave', leave);
      el.removeEventListener('touchstart', enter);
      el.removeEventListener('touchend', leave);
    };
  }

  function tilt(el, opts) {
    if (!hasHover()) { return; }
    opts = opts || {};
    var maxRotate = opts.maxRotate != null ? opts.maxRotate : 8;
    var perspective = opts.perspective || 800;
    var scale = opts.scale != null ? opts.scale : 1.02;
    var origTransform = el.style.transform || '';
    var origTransition = el.style.transition || '';

    function handleMove(e) {
      if (reducedMotion) { return; }
      var rect = el.getBoundingClientRect();
      var centerX = rect.left + rect.width / 2;
      var centerY = rect.top + rect.height / 2;
      var x = (e.clientX - centerX) / (rect.width / 2);
      var y = (e.clientY - centerY) / (rect.height / 2);
      el.style.transition = 'none';
      el.style.transform = 'perspective(' + perspective + 'px)'
        + ' rotateX(' + (-y * maxRotate).toFixed(2) + 'deg)'
        + ' rotateY(' + (x * maxRotate).toFixed(2) + 'deg)'
        + ' scale3d(' + scale + ', ' + scale + ', 1)';
    }

    function reset() {
      el.style.transition = 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)';
      el.style.transform = origTransform;
      setTimeout(function () { el.style.transition = origTransition; }, 500);
    }

    el.addEventListener('mousemove', handleMove, { passive: true });
    el.addEventListener('mouseleave', reset);

    el._bonsaitTiltCleanup = function () {
      el.removeEventListener('mousemove', handleMove);
      el.removeEventListener('mouseleave', reset);
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ── 3. Tactile Button & CTA Feedback ──────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════════════

  function press(el, callback) {
    function handlePress() {
      if (reducedMotion) { return; }
      var anim = el.animate([
        { transform: 'scale3d(1, 1, 1)' },
        { transform: 'scale3d(0.96, 0.96, 1)', offset: 0.4 },
        { transform: 'scale3d(1, 1, 1)' }
      ], { duration: 200, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' });
      if (callback) { anim.onfinish = callback; }
    }
    el.addEventListener('mousedown', handlePress);
    el.addEventListener('touchstart', handlePress, { passive: true });
  }

  function loadingButton(el, promise) {
    if (reducedMotion) { return promise; }
    var origText = el.textContent;
    var origWidth = el.offsetWidth;
    var origDisabled = el.disabled;

    el.disabled = true;
    el.style.minWidth = origWidth + 'px';
    el.style.position = 'relative';
    el.setAttribute('data-original-text', origText);
    el.innerHTML = '<span class="ba-spinner" style="'
      + 'display:inline-block;width:16px;height:16px;border:2px solid rgba(255,255,255,0.3);'
      + 'border-top-color:currentColor;border-radius:50%;animation:ba-spin 0.6s linear infinite;'
      + 'vertical-align:middle;"></span>';

    if (!document.getElementById('ba-spinner-style')) {
      var style = document.createElement('style');
      style.id = 'ba-spinner-style';
      style.textContent = '@keyframes ba-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(style);
    }

    return promise.finally(function () {
      el.innerHTML = origText;
      el.disabled = origDisabled;
      el.style.minWidth = '';
      el.style.position = '';
      el.removeAttribute('data-original-text');
    });
  }

  loadingButton.done = function (el, doneText) {
    el.innerHTML = '<span style="display:inline-flex;align-items:center;gap:4px;">'
      + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"'
      + ' stroke-width="3" stroke-linecap="round" stroke-linejoin="round"'
      + ' style="animation:ba-check-draw 0.3s ease-out forwards;">'
      + '<polyline points="20 6 9 17 4 12"/>'
      + '</svg>' + (doneText || '') + '</span>';

    if (!document.getElementById('ba-check-style')) {
      var style = document.createElement('style');
      style.id = 'ba-check-style';
      style.textContent = '@keyframes ba-check-draw{'
        + 'from{stroke-dasharray:24;stroke-dashoffset:24}'
        + 'to{stroke-dasharray:24;stroke-dashoffset:0}}';
      document.head.appendChild(style);
    }
    setTimeout(function () {
      el.disabled = false;
      el.style.minWidth = '';
      el.style.position = '';
    }, 1200);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ── 4. Skeleton Shimmer ───────────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════════════

  function shimmer(el, opts) {
    opts = opts || {};
    var lines = opts.lines || 3;
    var gap = opts.gap || 8;
    var height = opts.height || '14px';
    var color = opts.color || '#e2e8f0';
    var origHTML = el.innerHTML;
    var origMinHeight = el.style.minHeight;

    var skeletonHTML = '';
    for (var i = 0; i < lines; i++) {
      var width = (i === lines - 1 && opts.width) ? opts.width : '100%';
      skeletonHTML += '<div style="height:' + height + ';width:' + width + ';'
        + 'background:linear-gradient(90deg,' + color + ' 25%,'
        + lightenColor(color, 0.6) + ' 50%,' + color + ' 75%);'
        + 'background-size:200% 100%;animation:ba-shimmer 1.5s ease-in-out infinite;'
        + 'border-radius:4px;'
        + (i < lines - 1 ? 'margin-bottom:' + gap + 'px;' : '')
        + '"></div>';
    }

    el.innerHTML = skeletonHTML;
    el.style.minHeight = el.style.minHeight || el.offsetHeight + 'px';

    if (!document.getElementById('ba-shimmer-style')) {
      var style = document.createElement('style');
      style.id = 'ba-shimmer-style';
      style.textContent = '@keyframes ba-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}';
      document.head.appendChild(style);
    }

    return {
      destroy: function () {
        el.innerHTML = origHTML;
        el.style.minHeight = origMinHeight;
      }
    };
  }

  function lightenColor(hex, amount) {
    hex = hex.replace('#', '');
    if (hex.length === 3) { hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2]; }
    var r = Math.min(255, parseInt(hex.substring(0,2), 16) + Math.round(255 * amount));
    var g = Math.min(255, parseInt(hex.substring(2,4), 16) + Math.round(255 * amount));
    var b = Math.min(255, parseInt(hex.substring(4,6), 16) + Math.round(255 * amount));
    return '#' + [r,g,b].map(function(c){return c.toString(16).padStart(2,'0');}).join('');
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ── 5. Micro-Form Field State Transitions ──────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════════════

  function fieldValidate(el, type, opts) {
    if (reducedMotion) { return; }
    opts = opts || {};
    if (type === 'error') {
      var origB = el.style.borderColor, origS = el.style.boxShadow;
      el.style.borderColor = '#ef4444';
      el.style.boxShadow = '0 0 0 3px rgba(239,68,68,0.2)';
      var anim = createAnimation(el, 'shake-x', { duration: 500 });
      if (anim) { anim.onfinish = function(){ el.style.borderColor=origB; el.style.boxShadow=origS; }; }
      else { setTimeout(function(){ el.style.borderColor=origB; el.style.boxShadow=origS; }, 500); }
    }
    if (type === 'success') {
      var origB2 = el.style.borderColor, origS2 = el.style.boxShadow;
      el.style.borderColor = '#22c55e';
      el.style.boxShadow = '0 0 0 3px rgba(34,197,94,0.2)';
      el.animate([{transform:'scale(1)'},{transform:'scale(1.02)',offset:0.3},{transform:'scale(1)'}],
        {duration:400,easing:'cubic-bezier(0.22,1,0.36,1)'});
      setTimeout(function(){ el.style.borderColor=origB2; el.style.boxShadow=origS2; }, 1200);
    }
  }

  function fieldFloat(input, opts) {
    opts = opts || {};
    var label;
    if (opts.labelSelector) {
      var p = input.closest('.floating-field, .input-group, .form-group');
      if (p) { label = p.querySelector(opts.labelSelector); }
    }
    if (!label) { label = input.parentElement ? input.parentElement.querySelector('label') : null; }
    if (!label) { return; }

    var dur = opts.duration || 200;
    var easing = parseEasing(opts.easing || 'cubic-bezier(0.22, 1, 0.36, 1)');
    label.style.transition = 'transform ' + dur + 'ms ' + easing + ', font-size ' + dur + 'ms ' + easing + ', color ' + dur + 'ms ' + easing;

    function check() {
      var v = input.value && input.value.length > 0;
      var f = document.activeElement === input;
      if (v || f) {
        label.style.transform = 'translate3d(0, -20px, 0) scale(0.85)';
        label.style.color = opts.activeColor || '#3b82f6';
      } else {
        label.style.transform = 'translate3d(0, 0, 0) scale(1)';
        label.style.color = '';
      }
    }
    input.addEventListener('focus', check);
    input.addEventListener('blur', check);
    input.addEventListener('input', check);
    check();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Intersection Observer
  // ═══════════════════════════════════════════════════════════════════════════

  var observer;
  var observedElements = new Map();

  function ensureObserver(opts) {
    if (observer) { return; }
    opts = opts || {};
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) { return; }
        var el = entry.target;
        var data = observedElements.get(el);
        if (!data || data.triggered) { return; }
        data.triggered = true;
        observer.unobserve(el);
        if (data.stagger) { staggerChildren(el, data.preset, data.opts); }
        else { createAnimation(el, data.preset, data.opts); }
      });
    }, {
      rootMargin: opts.rootMargin || '0px 0px -50px 0px',
      threshold: opts.threshold || 0.1
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Main init
  // ═══════════════════════════════════════════════════════════════════════════

  function init(opts) {
    opts = opts || {};
    if (reducedMotion) {
      document.querySelectorAll('[data-animate]').forEach(function (el) {
        el.style.opacity = '1'; el.style.transform = 'none'; el.style.visibility = 'visible';
      });
      return;
    }
    ensureObserver(opts);

    document.querySelectorAll('[data-animate]').forEach(function (el) {
      var presetName = el.getAttribute('data-animate');
      var trigger = el.getAttribute('data-animate-trigger') || 'scroll';
      var stagger = el.getAttribute('data-animate-stagger');
      var staggerSelector = el.getAttribute('data-animate-stagger-selector') || null;
      var scrollLinked = el.getAttribute('data-animate-scroll-linked');

      var animOpts = {
        duration: parseInt(el.getAttribute('data-animate-duration'), 10) || undefined,
        delay: parseInt(el.getAttribute('data-animate-delay'), 10) || 0,
        easing: el.getAttribute('data-animate-easing') || undefined,
        iterations: (function () {
          var rep = el.getAttribute('data-animate-repeat');
          if (rep === 'infinite' || rep === '-1') { return Infinity; }
          var n = parseInt(rep, 10); return isNaN(n) ? 1 : n;
        })(),
        direction: el.getAttribute('data-animate-direction') || 'normal',
        stagger: stagger ? parseInt(stagger, 10) : 0,
        staggerSelector: staggerSelector,
        scrollLinked: scrollLinked === 'true' || scrollLinked === '1',
        scrollStart: parseFloat(el.getAttribute('data-animate-scroll-start')) || 0,
        scrollEnd: parseFloat(el.getAttribute('data-animate-scroll-end')) || 1,
        scrollClamp: el.getAttribute('data-animate-scroll-clamp') !== 'false'
      };

      var mappedName = ANIMATE_CSS_MAP[presetName] || presetName;

      switch (trigger) {
        case 'load':
          requestAnimationFrame(function () {
            if (stagger) { staggerChildren(el, mappedName, animOpts); }
            else { createAnimation(el, mappedName, animOpts); }
          });
          break;
        case 'hover':
          var hoverAnim = null;
          el.addEventListener('mouseenter', function () {
            if (hoverAnim) { hoverAnim.cancel(); }
            hoverAnim = createAnimation(el, mappedName, animOpts);
          });
          el.addEventListener('mouseleave', function () { if (hoverAnim) { hoverAnim.reverse(); } });
          if (mappedName === 'elevate') { elevate(el, animOpts); }
          if (mappedName === 'tilt') { tilt(el, animOpts); }
          break;
        case 'click':
          el.addEventListener('click', function () { createAnimation(el, mappedName, animOpts); });
          if (mappedName === 'press') { press(el); }
          break;
        case 'scroll':
        default:
          if (scrollLinked) { createScrollLinked(el, mappedName, animOpts); }
          else {
            observedElements.set(el, {
              preset: mappedName, opts: animOpts, triggered: false,
              stagger: stagger ? parseInt(stagger, 10) : 0
            });
            observer.observe(el);
          }
          break;
      }
    });

    // Legacy [data-appear-animation] elements are handled by the site's own
    // inline scroll script — do NOT process them here to avoid double animation.
    // Bonsait animations only process elements with [data-animate] attribute.
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Public API
  // ═══════════════════════════════════════════════════════════════════════════

export var Animations = {
  init: init,
  create: createAnimation,
  stagger: staggerChildren,
  scrollLinked: createScrollLinked,
  animateOut: animateOut,
  springEasing: springEasing,
  presets: PRESETS,
  reveal: reveal,
  elevate: elevate,
  tilt: tilt,
  unElevate: function (el) { if (el._bonsaitElevateCleanup) { el._bonsaitElevateCleanup(); } },
  unTilt: function (el) { if (el._bonsaitTiltCleanup) { el._bonsaitTiltCleanup(); } },
  press: press,
  loadingButton: loadingButton,
  shimmer: shimmer,
  fieldValidate: fieldValidate,
  fieldFloat: fieldFloat,
  pause: function (el) { if (el._bonsaitAnimation) { el._bonsaitAnimation.pause(); } },
  play: function (el) { if (el._bonsaitAnimation) { el._bonsaitAnimation.play(); } },
  reverse: function (el) { if (el._bonsaitAnimation) { el._bonsaitAnimation.reverse(); } },
  cancel: function (el) { if (el._bonsaitAnimation) { el._bonsaitAnimation.cancel(); } },
  finish: function (el) { if (el._bonsaitAnimation) { el._bonsaitAnimation.finish(); } }
};

// Backward-compat: expose on window for editor/legacy consumers that don't use the Bonsait namespace
window.BonsaitAnimations = Animations;

// Auto-init: wire up all [data-animate] elements when the DOM is ready.
// Legacy [data-appear-animation] elements are handled by the site's own scroll script.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () { Animations.init(); });
} else {
  Animations.init();
}
